import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';

interface BrandData {
  company?: { name?: string; description?: string };
  branding?: {
    colors?: string[];
    logos?: Record<string, string>;
    typefaces?: string[];
  };
  photos?: Array<{ url?: string; alt_text?: string }>;
}
const pairs: Record<string, [string, string]> = {
  editorial: ['Libre Baskerville', 'Source Sans 3'],
  industrial: ['Barlow Condensed', 'Barlow'],
  neighborhood: ['Bree Serif', 'Nunito Sans'],
  architectural: ['Cormorant Garamond', 'Manrope'],
  technical: ['Space Grotesk', 'Source Sans 3'],
};

export default function BrandedDesignComparison({
  data,
  family,
  onFamily,
  fontPair,
  onFontPair,
  primaryColor,
  onPrimaryColor,
  heroStyle,
}: {
  data?: BrandData;
  family: string;
  onFamily: (family: string) => void;
  fontPair: string;
  onFontPair: (value: string) => void;
  primaryColor: string;
  onPrimaryColor: (value: string) => void;
  heroStyle: string;
}) {
  const [mobile, setMobile] = useState(false);
  const options = Array.from(
    new Set([
      family === 'auto' ? 'editorial' : family,
      'industrial',
      'neighborhood',
      'architectural',
    ]),
  ).slice(0, 3);
  const name = data?.company?.name || 'Your roofing company';
  const copy =
    data?.company?.description?.slice(0, 130) ||
    'Roofing expertise, personal service, and workmanship you can count on.';
  const photo = data?.photos?.find((item) => item.url);
  const logo =
    data?.branding?.logos?.logo_wide_full_color ||
    data?.branding?.logos?.logo_square_full_color;
  const color =
    primaryColor ||
    data?.branding?.colors?.find((value) => /^#[0-9a-f]{6}$/i.test(value)) ||
    '#5b35a8';
  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', mb: 2 }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography level="title-lg">Compare with your brand</Typography>
            <Typography level="body-sm">
              The same logo, photo and copy in three different compositions.
            </Typography>
          </Box>
          <Button
            size="sm"
            variant="outlined"
            onClick={() => setMobile((value) => !value)}
          >
            {mobile ? 'Show desktop concepts' : 'Show mobile concepts'}
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {options.map((option) => {
            const dark = option === 'industrial';
            const warm = option === 'neighborhood';
            const headingFont = (pairs[fontPair || option] ||
              pairs.editorial)[0];
            return (
              <Sheet
                key={option}
                variant="outlined"
                sx={{
                  borderRadius: 'md',
                  overflow: 'hidden',
                  borderWidth: family === option ? 2 : 1,
                  borderColor: family === option ? 'primary.500' : 'divider',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: dark ? '#17242b' : warm ? '#fff7ed' : '#fafafa',
                    color: dark ? '#ffffff' : '#18242b',
                    minHeight: 320,
                    maxWidth: mobile ? 280 : '100%',
                    mx: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: dark ? '#ffffff44' : '#18242b33',
                      pb: 1,
                      mb: 2,
                    }}
                  >
                    {logo ? (
                      <Box
                        component="img"
                        src={logo}
                        alt={name}
                        sx={{
                          height: 30,
                          maxWidth: '100%',
                          objectFit: 'contain',
                          bgcolor: '#fff',
                          borderRadius: 'sm',
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{ color: 'inherit', fontWeight: 800, fontSize: 12 }}
                      >
                        {name}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        !mobile && (dark || heroStyle === 'split_layout')
                          ? '1.1fr 1fr'
                          : '1fr',
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: `${headingFont}, ${warm || option === 'editorial' ? 'Georgia, serif' : 'Arial, sans-serif'}`,
                          color: 'inherit',
                          fontSize: dark ? 30 : 27,
                          fontWeight: dark ? 900 : 500,
                          textTransform: dark ? 'uppercase' : 'none',
                          lineHeight: 1.1,
                        }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        sx={{ color: 'inherit', fontSize: 11, mt: 1 }}
                      >
                        {copy}
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: color,
                          color: '#fff',
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.75,
                          my: 1,
                          borderRadius: warm ? 20 : 0,
                          fontSize: 11,
                        }}
                      >
                        Get a free estimate
                      </Box>
                    </Box>
                    {photo ? (
                      <Box
                        component="img"
                        src={photo.url}
                        alt={photo.alt_text || 'Your project photo'}
                        sx={{
                          width: '100%',
                          height: mobile ? 95 : 120,
                          objectFit: 'cover',
                          borderRadius: warm ? '40px 40px 6px 6px' : 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 100,
                          bgcolor: color,
                          opacity: 0.4,
                          borderRadius: warm ? '40px 40px 6px 6px' : 0,
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        option === 'technical' ? '1fr' : 'repeat(3, 1fr)',
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    {['Expertise', 'Projects', 'Our promise'].map((text) => (
                      <Box
                        key={text}
                        sx={{
                          borderTop: `2px solid ${color}`,
                          pt: 0.5,
                          fontSize: 10,
                        }}
                      >
                        {text}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Button
                  fullWidth
                  variant={family === option ? 'solid' : 'plain'}
                  onClick={() => onFamily(option)}
                >
                  {family === option ? 'Selected' : 'Choose'} {option}
                </Button>
              </Sheet>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl>
            <FormLabel>Keep this typography on regeneration</FormLabel>
            <Select
              value={fontPair}
              onChange={(_, value) => onFontPair(value || '')}
            >
              <Option value="">Follow brand / chosen direction</Option>
              {Object.entries(pairs).map(([key, fonts]) => (
                <Option key={key} value={key}>
                  {fonts.join(' + ')}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Primary color lock</FormLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Input
                type="color"
                aria-label="Locked primary color"
                value={color}
                onChange={(event) => onPrimaryColor(event.target.value)}
              />
              <Button
                size="sm"
                variant="plain"
                disabled={!primaryColor}
                onClick={() => onPrimaryColor('')}
              >
                Follow brand
              </Button>
            </Box>
          </FormControl>
        </Box>
        <Alert color="neutral">
          These are composition previews, not generated sites. Comparing is free
          and makes no AI calls. Your hero selection below, chosen direction and
          locks are saved for generation; final content and layout are reviewed
          in the generated preview.
        </Alert>
      </Stack>
    </Sheet>
  );
}
